import { GeoJSONFeatureCollection } from 'ol/format/GeoJSON';
import { Chart, ChartDataset } from "chart.js";
import ElevationDataHelper from "./ElevationDataHelper";
import _ from "lodash";
import simplify from 'simplify-js';
import { containsCoordinate, Extent } from 'ol/extent';
import ElevDataPoint from './ElevDataPoint';

export interface ElevationChartDataInfo {
    dataSetStandard: ElevDataPoint[];
    dataSetSimplifiedForGradientCalc: ElevDataPoint[];
}

export default class ElevationChartHelper {

    static async getElevationChartData(track: GeoJSONFeatureCollection, visibleExtent: Extent, pxWidth: number): Promise<ElevationChartDataInfo> {
        let chartPoints: ElevDataPoint[] = []

        for (const segment of track.features) {
            const elevationData = await ElevationDataHelper.getElevationData(segment)
            chartPoints = chartPoints.concat(elevationData)
        }

        // Compute each point's x coord as distance along overall track rather than within each segment. 
        // Initially, each concatenated segment begins with x==0 
        let base = 0       
        for (let i=1; i < chartPoints.length; i++) {
            if (chartPoints[i].x == 0) base = chartPoints[i-1].x
            chartPoints[i].x += base
        }

        // Divide the track into segments that are visible on the map
        const visibleSegments = this.visibleElevationTrackSegs (chartPoints, visibleExtent)
        
        let dataSetSimplifiedForGradientCalc: ElevDataPoint[] = []
        chartPoints = []
        if (visibleSegments.length > 0) { // only if there is at least one viisble segment
            const firstSeg = visibleSegments[0], lastSeg = _.last(visibleSegments)
            const visibleLength = _.last(lastSeg)!.x - firstSeg[0].x

            // Reduce each visible segment to a number of points that is fewer than the number of pixels 
            // available to draw it on the chart, and join them together into one dataset again with (NaN,NaN)
            // points indicating where the visible track goes temporarily off the visible map
            for (const visSeg of visibleSegments) {
                if (chartPoints.length) chartPoints.push({x: NaN, y: NaN, mapPosition: undefined, gradient:0})
                const reduced = this.reduce(visSeg, visibleLength, pxWidth)
                chartPoints = chartPoints.concat(reduced) 
            }

            dataSetSimplifiedForGradientCalc = simplify(_.cloneDeep(chartPoints), 30, true) as ElevDataPoint[]
            this.computeGradient (dataSetSimplifiedForGradientCalc)
        }

        return {
            dataSetStandard: chartPoints,
            dataSetSimplifiedForGradientCalc: dataSetSimplifiedForGradientCalc
        }
    }

    // Divide the track into segments that are visible on the map In effect, "zoom" the elevation chart 
    // data to match what is visible on the map. Segments of track that lie outside the visible map area 
    // will eventually be represented by a (NaN,NaN) point, which creates a gap on the elevation chart.
    static visibleElevationTrackSegs (wholeTrack: ElevDataPoint[], visibleExtent: Extent): ElevDataPoint[][] {

        const visibleSegments: ElevDataPoint[][] = []
        
        const firstVisiblePoint = wholeTrack.findIndex(p => containsCoordinate(visibleExtent, p.mapPosition!));
        const lastVisiblePoint = wholeTrack.findLastIndex(p => containsCoordinate(visibleExtent, p.mapPosition!));
        visibleSegments.push(wholeTrack.slice(firstVisiblePoint, lastVisiblePoint));

        // the following algorithm is what it used to do - which is to put gaps in the elevation chart if it went off 
        // the map and then back on again. I now think this looks it look more messy than is useful

        // let firstVisible = undefined               // index of 1st point in a visible section of the route
        // const lastPoint = wholeTrack.length-1      // index of the last point of the route
        // for (let i=0; i < wholeTrack.length; i++) {
        //     // TODO there is an inaccuracy where the track goes off the edge of an OS map, but it's OK for the other renderers 
        //     //      - something to do with renderer projection or scale? or the tiles at different resolutions?
        //     //      - the map & elev chart visualisations don't seem to match where the track leaves the visible map area
        //     const thisPointisVisibleOnTheMap = containsCoordinate(visibleExtent, wholeTrack[i].mapPosition!)
        //     if (firstVisible === undefined) {
        //         if (thisPointisVisibleOnTheMap) { 
        //             firstVisible =  i; 
        //         }
        //     } 
        //     else {
        //         if (!thisPointisVisibleOnTheMap || i == lastPoint) { 
        //             visibleSegments.push(wholeTrack.slice (firstVisible, i+1))
        //             firstVisible = undefined
        //         } 
        //     }
        // }

        return visibleSegments
    }

    // Reduce the dataset so that the number of points is less than the number of pixels available in the width of the chart that will display them
    // Just do this by iteratively searching for a "tolerance" parameter for the simplify() algorithm. This approach sounds inefficient, but it's 
    // MUCH MUCH faster than I expected - the simplify() algorithm is blisteringly fast!
    // Several reasons why we need to reduce the number of points:
    // (1) performance
    // (2) chart.js has its own "decimation" algorithm to reduce large datasets into something tha can be plotted in a smaller pixel width, but that
    //     algorithm won't match our calculation of gradients at each point so that the gradient colour may not match the visible slope of the chart 
    // (3) Another aspect of the same problem, multiple data points for gradient colour chading will colide/overwrite on the same x-pixel position, 
    //     so that the gradient colour matches *one of* the gradients at that posiiton, but not necessarily matching the chart polyline
    // Also, simplify() is not a continuous function so there is usually no tolerance value that will reduces the data set exactly to the target number
    // of data points, so just try to get the highest number that is less than the desired number. The data points are not evenly spaced anyway (the
    // x-axis here is distance along the track, not a map coordinate), and their spacing will vary more after simplify() has removed some points so we 
    // should aim for less points than the available pixel width, so somewhat arbitrarily aim for 95% of target. Often, this will be catered-for 
    // already by the non-continuous nature of the simplify() function anyway, but not always (especially in large datasets)
    static reduce(points: ElevDataPoint[], trkLen: number, pxwidth: number): ElevDataPoint[] {

        // trklen: length of the overall [visible] track, of which these elevation points may only be one visible part
        // seglen: length of this [visible] segment of the track (which may not span for the whole width of the chart)
        // pxwidth: number of pixels available in the full width of the chart area
        // target: number of data points to reduce to, fewer than one per pixel on the chart
        const segLen = _.last(points)!.x - points[0].x 
        const target = (segLen / trkLen) * pxwidth * 0.95 // 95% as discussed in comment above

        if (points.length < target) { 
            return points 
        }

        let minTolerance = 0, maxTolerance = 20
        let tolerance = (minTolerance + maxTolerance) / 2

        let reduced = points
        let elapsed = 0, prevcount = 0

        // Keep trying for up to 500ms, but usually it finishes in <10 iterations in <10ms
        const then = new Date().getTime()
        for (; elapsed < 500; ) {
            elapsed = new Date().getTime() - then        
            reduced = simplify (points, tolerance, true) as ElevDataPoint[]
            
            if (reduced.length == prevcount) break; else prevcount = reduced.length // abandon if it's not improving, ie there is no tolerance value that can get closer
            if (reduced.length > target) { minTolerance = tolerance; tolerance = maxTolerance-(maxTolerance-tolerance)/2; continue }
            if (reduced.length < target) { maxTolerance = tolerance; tolerance = minTolerance+(tolerance-minTolerance)/2; continue }
            break;
        }
        
        return reduced
    }

    static getIndicatorDataset(chart: Chart): ChartDataset {
        const indicatorDataset = _.last(chart.data.datasets)
        if(!indicatorDataset) {
            throw new Error("Indicator dataset not found (no datasets?)")
        }
        return indicatorDataset
    }

    // Compute the gradient at each point in the elevation data series - original algorithm
    // Gradient is computed laast (afterinterpolate, simplify and smooth) so that it matches the displayed line
    static computeGradient (chartPoints: ElevDataPoint[]) {
        for (let head = 0, tail = 0; head < chartPoints.length; head++, tail = head-1) {
            chartPoints[head].gradient = ( (chartPoints[head].y - chartPoints[tail].y) / (chartPoints[head].x - chartPoints[tail].x) ) || 0
        }    
    }

    // Compute the gradient at each point in the elevation data series - Ben's algorithm 1-Feb-22
    // Gradient is computed laast (afterinterpolate, simplify and smooth) so that it matches the displayed line
    static computeGradient2 (chartPoints: ElevDataPoint[]) {       
        let calcGradientOver = 500
        const firstNonNanPoint = _.find(chartPoints, p => !isNaN(p.x))
        const lastNonNanPoint = _.findLast(chartPoints, p => !isNaN(p.x))
        if(firstNonNanPoint && lastNonNanPoint) {
            calcGradientOver = Math.min(calcGradientOver, (lastNonNanPoint.x - firstNonNanPoint.x) / 10)
        }
        // Compute gradient 
        for (let head = 0, tail = 0; head < chartPoints.length; head++) {
            while(tail < head - 1 && chartPoints[head].x - chartPoints[tail].x > calcGradientOver) {
                tail++
            }
            chartPoints[head].gradient = ( (chartPoints[head].y - chartPoints[tail].y) / (chartPoints[head].x - chartPoints[tail].x) ) || 0
        }
    }
            
}
